import React, { Suspense, useState, useEffect, useRef } from "react";
import { BrickGrid } from "@/components/BrickGrid";

const Spline = React.lazy(() => import("@splinetool/react-spline"));

class SplineBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) return this.props.fallback;
        return this.props.children;
    }
}

const isLiteDevice = () => {
    if (typeof window === "undefined") return false;
    try {
        const small = window.matchMedia("(max-width: 820px)").matches;
        const coarse = window.matchMedia("(pointer: coarse)").matches;
        const lowMem = (navigator.deviceMemory || 8) <= 4;
        return small || coarse || lowMem;
    } catch (e) {
        return false;
    }
};

// The lightweight BrickGrid renders instantly; the heavy Spline 3D scene is
// deferred until the page has fully loaded and the browser is idle, then it
// fades in. It is also hidden (compositing paused) when the hero scrolls out
// of view, so it never competes for the GPU during scroll.
export const SplineHero = () => {
    const rootRef = useRef(null);
    const [show, setShow] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [inView, setInView] = useState(true);

    useEffect(() => {
        if (isLiteDevice()) return; // mobile / low-end: skip the heavy 3D entirely for speed
        let handle;
        const ric = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), 1200));
        const cic = window.cancelIdleCallback || clearTimeout;
        const start = () => {
            handle = ric(() => setShow(true), { timeout: 3000 });
        };
        if (document.readyState === "complete") start();
        else window.addEventListener("load", start, { once: true });
        return () => {
            if (handle) cic(handle);
            window.removeEventListener("load", start);
        };
    }, []);

    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin: "120px" });
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <div ref={rootRef} className="absolute inset-0">
            {/* Lightweight interactive base — paints immediately */}
            <BrickGrid />
            {/* Heavy 3D scene — deferred, GPU-composited, paused when off-screen */}
            {show && (
                <div
                    className={`absolute inset-0 mix-blend-screen transform-gpu transition-opacity duration-1000 ${loaded && inView ? "opacity-70" : "opacity-0"} ${inView ? "" : "hidden"}`}
                >
                    <SplineBoundary fallback={null}>
                        <Suspense fallback={null}>
                            <Spline
                                scene="https://prod.spline.design/Slk6b8kz3LRlKiyk/scene.splinecode"
                                className="h-full w-full"
                                onLoad={() => setLoaded(true)}
                            />
                        </Suspense>
                    </SplineBoundary>
                </div>
            )}
        </div>
    );
};
